/**
 * ZOAuth - Unified OAuth Domain
 *
 * Single source of truth for ALL OAuth operations (Documents + Deployments).
 * Both use the same OAuthConnection table for persistent token storage.
 *
 * Provider Types:
 * - Document providers: GOOGLE_DRIVE, SHAREPOINT, NOTION
 * - Deployment providers: GMAIL, OUTLOOK, SLACK, TEAMS, WHATSAPP, TELEGRAM
 *
 * Flow:
 * 1. Start OAuth → Backend generates state, returns authorization URL
 * 2. Provider callback → Backend forwards `code` + `state` to the frontend (no exchange)
 * 3. Complete → the frontend, signed in as the user who started, posts them back;
 *    the backend exchanges the code and stores tokens → OAuthConnection table
 * 4. Frontend notified → WebSocket `handshake:completed` event
 *
 * Naming: Z prefix = Zod schemas (vs TApi = TypeScript types)
 */

import { ApiSuccessSchema } from "../../common/response.schema";
import {
  BackfillAccountInfoPathVarsSchema,
  BackfillAccountInfoResponseSchema,
  DeleteConnectionPathVarsSchema,
  DeleteConnectionResponseSchema,
  ListConnectionsResponseSchema,
  OAuthCompleteBodySchema,
  OAuthCompleteResponseSchema,
  OAuthStartBodySchema,
  OAuthStartResponseSchema
} from "./schemas";

/**
 * ZOAuth - Unified OAuth API validation schemas
 *
 * All providers use unified /api/oauth/* routes:
 * - POST /api/oauth/start (all providers)
 * - GET /api/oauth/callback/:provider (forwards to the frontend, completes nothing)
 * - POST /api/oauth/complete
 * - GET /api/oauth/connections
 * - DELETE /api/oauth/connections/:provider
 */
export const ZOAuth = {
  // ============================================================================
  // UNIFIED OAUTH FLOW (Routes: /api/oauth/*)
  // ============================================================================

  Start: {
    method: "POST" as const,
    path: "/api/oauth/start",
    description: "Start OAuth flow for any provider (Notion, Google Drive, SharePoint, Gmail)",
    Body: OAuthStartBodySchema,
    Response: ApiSuccessSchema(OAuthStartResponseSchema)
  },

  /**
   * Complete a flow the SIGNED-IN caller started: exchange the code the provider
   * returned to their browser. A flow another user started — in this organization
   * or any other — is refused exactly like one that does not exist.
   */
  Complete: {
    method: "POST" as const,
    path: "/api/oauth/complete",
    description: "Complete an OAuth flow the signed-in user started",
    Body: OAuthCompleteBodySchema,
    Response: ApiSuccessSchema(OAuthCompleteResponseSchema)
  },

  // ============================================================================
  // CONNECTION MANAGEMENT (OAuthConnection table - ALL providers)
  // ============================================================================

  ListConnections: {
    method: "GET" as const,
    path: "/api/oauth/connections",
    description: "List all OAuth connections for current organization",
    Response: ApiSuccessSchema(ListConnectionsResponseSchema)
  },

  DeleteConnection: {
    method: "DELETE" as const,
    path: "/api/oauth/connection/:connectionId",
    description: "Delete specific OAuth connection by ID (multi-account safe)",
    PathVars: DeleteConnectionPathVarsSchema,
    Response: ApiSuccessSchema(DeleteConnectionResponseSchema)
  },

  BackfillAccountInfo: {
    method: "POST" as const,
    path: "/api/oauth/connection/:connectionId/backfill-account-info",
    description: "Fetch and store user info for connections missing email/name/avatar",
    PathVars: BackfillAccountInfoPathVarsSchema,
    Response: ApiSuccessSchema(BackfillAccountInfoResponseSchema)
  }
} as const;
